import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { parseZipTextToUniqueZips } from "../../services/mapCarousel";
import { fetchZctaAcsSnapshot } from "../../services/census/fetchZctaAcsSnapshot";
import { acsFiveYearVintageLabel } from "../../services/census/acsReleaseConfig";

const FETCH_CONCURRENCY = 5;

/** @typedef {'percent' | 'currency' | 'integer'} ZctaMetricFormat */

/** @type {{ key: string, label: string, format: ZctaMetricFormat }[]} */
const ZCTA_FOCUS_METRICS = [
  { key: "pctMale", label: "% male (total pop.)", format: "percent" },
  { key: "pctFemale", label: "% female (total pop.)", format: "percent" },
  { key: "medianHouseholdIncome", label: "Median household income", format: "currency" },
  { key: "totalPopulation", label: "Total population", format: "integer" },
  { key: "pctAge65Plus", label: "% age 65+", format: "percent" },
  { key: "pctPrivateInsurance", label: "% private insurance", format: "percent" },
  { key: "pctPublicCoverage", label: "% public coverage", format: "percent" },
  { key: "pctUninsured", label: "% uninsured", format: "percent" },
  { key: "pctInsuredApprox", label: "% insured (approx.)", format: "percent" },
  { key: "pctMedicaidMeansTested", label: "% Medicaid / means-tested public", format: "percent" },
];

function defaultMetricKey() {
  return ZCTA_FOCUS_METRICS[0]?.key ?? "pctMale";
}

/**
 * @param {number|null|undefined} n
 * @param {ZctaMetricFormat} format
 */
function formatMetricDisplay(n, format) {
  if (n == null || !Number.isFinite(n)) return "—";
  if (format === "percent") return `${n.toFixed(1)}%`;
  if (format === "currency") return `$${Math.round(n).toLocaleString()}`;
  return `${Math.round(n).toLocaleString()}`;
}

/** @param {Record<string, unknown>|null|undefined} snap */
function metricNumeric(snap, metricKey) {
  if (!snap || typeof snap !== "object") return null;
  const v = snap[metricKey];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function sortZipsByMetric(zips, snapshots, metricKey, ascending) {
  return [...zips].sort((a, b) => {
    const na = metricNumeric(snapshots[a], metricKey);
    const nb = metricNumeric(snapshots[b], metricKey);
    const aNull = na == null;
    const bNull = nb == null;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    return ascending ? na - nb : nb - na;
  });
}

async function fetchSnapshotsBatched(zips, onProgress) {
  /** @type {Record<string, object|null>} */
  const out = {};
  for (let i = 0; i < zips.length; i += FETCH_CONCURRENCY) {
    const chunk = zips.slice(i, i + FETCH_CONCURRENCY);
    const results = await Promise.all(
      chunk.map(async (z) => {
        try {
          return await fetchZctaAcsSnapshot(z);
        } catch (e) {
          console.warn("ZipTargetingField: ACS fetch failed", z, e);
          return null;
        }
      })
    );
    chunk.forEach((z, j) => {
      out[z] = results[j];
    });
    onProgress({ ...out });
  }
  return out;
}

/**
 * US ZCTA list: delete-only, optional ACS focus metric bar chart, sort by metric.
 * Add codes via map or saved collections (not this field).
 *
 * @param {{
 *   value: string,
 *   onChange: (next: string) => void,
 *   disabled?: boolean,
 *   label?: string,
 *   minRows?: number,
 *   showQuickAdd?: boolean,
 *   sx?: object,
 * }} props
 */
const ZipTargetingField = ({
  value,
  onChange,
  disabled = false,
  label = "US ZCTA / Census areas in this group",
  minRows: _minRows,
  showQuickAdd: _showQuickAdd,
  sx = {},
}) => {
  const theme = useTheme();
  const unique = useMemo(() => parseZipTextToUniqueZips(value), [value]);
  const zipsFetchKey = useMemo(() => unique.join("\u0001"), [unique]);
  const [focusMetric, setFocusMetric] = useState(defaultMetricKey);
  const [snapshots, setSnapshots] = useState({});
  const [loadingAcs, setLoadingAcs] = useState(false);

  const metricMeta = useMemo(
    () => ZCTA_FOCUS_METRICS.find((m) => m.key === focusMetric) ?? ZCTA_FOCUS_METRICS[0],
    [focusMetric]
  );

  useEffect(() => {
    let cancelled = false;
    if (unique.length === 0) {
      setSnapshots({});
      setLoadingAcs(false);
      return undefined;
    }
    setLoadingAcs(true);
    setSnapshots({});
    void (async () => {
      await fetchSnapshotsBatched(unique, (partial) => {
        if (!cancelled) setSnapshots(partial);
      });
      if (!cancelled) setLoadingAcs(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [zipsFetchKey]);

  const removeZip = useCallback(
    (z) => {
      const next = unique.filter((x) => x !== z);
      onChange(next.join("\n"));
    },
    [unique, onChange]
  );

  const applySort = useCallback(
    (ascending) => {
      const sorted = sortZipsByMetric(unique, snapshots, focusMetric, ascending);
      onChange(sorted.join("\n"));
    },
    [unique, snapshots, focusMetric, onChange]
  );

  const chartData = useMemo(() => {
    if (!metricMeta || unique.length === 0) return [];
    return unique.map((z) => {
      const snap = snapshots[z];
      const raw = metricNumeric(snap, focusMetric);
      return {
        zcta: z,
        value: raw,
        chartValue: raw == null ? null : raw,
        label: formatMetricDisplay(raw, metricMeta.format),
      };
    });
  }, [unique, snapshots, focusMetric, metricMeta]);

  const chartHeight = Math.min(520, Math.max(140, unique.length * 26 + 80));

  const chartTooltip = useCallback(
    ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
          }}
        >
          <Typography variant="caption" display="block" fontWeight={600}>
            ZCTA {row.zcta}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {metricMeta?.label}: {row.label}
          </Typography>
        </Box>
      );
    },
    [metricMeta]
  );

  return (
    <Box sx={sx}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        <strong>{unique.length}</strong> unique US ZCTA {unique.length === 1 ? "code" : "codes"}.
        Add codes from the map or a saved collection; remove codes with the trash control. Insurance
        percentages are usually civilian noninstitutionalized population; age % is often total
        population (ACS).
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <FormControl size="small" sx={{ minWidth: 280 }} disabled={disabled}>
          <InputLabel id="zcta-focus-metric-label">Focus metric</InputLabel>
          <Select
            labelId="zcta-focus-metric-label"
            label="Focus metric"
            value={focusMetric}
            onChange={(e) => setFocusMetric(String(e.target.value))}
          >
            {ZCTA_FOCUS_METRICS.map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Sort list low → high by this metric (missing data last)">
            <span>
              <IconButton
                size="small"
                aria-label="Sort ascending by focus metric"
                disabled={disabled || unique.length < 2}
                onClick={() => applySort(true)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Sort list high → low by this metric (missing data last)">
            <span>
              <IconButton
                size="small"
                aria-label="Sort descending by focus metric"
                disabled={disabled || unique.length < 2}
                onClick={() => applySort(false)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {loadingAcs ? (
            <CircularProgress size={22} sx={{ ml: 0.5 }} aria-label="Loading Census data" />
          ) : null}
        </Stack>
      </Stack>

      {unique.length > 0 ? (
        <Box sx={{ width: "100%", height: chartHeight, maxWidth: "100%", mb: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                type="number"
                tick={{ fontSize: 11 }}
                stroke={theme.palette.text.secondary}
                domain={[0, "auto"]}
              />
              <YAxis
                type="category"
                dataKey="zcta"
                width={52}
                tick={{ fontSize: 11 }}
                stroke={theme.palette.text.secondary}
              />
              <RechartsTooltip content={chartTooltip} />
              <Bar
                dataKey="chartValue"
                name={metricMeta?.label}
                fill={theme.palette.primary.main}
                radius={[0, 4, 4, 0]}
                isAnimationActive={unique.length <= 40}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {acsFiveYearVintageLabel()}
      </Typography>

      {unique.length > 0 ? (
        <List
          dense
          disablePadding
          sx={{
            maxHeight: 280,
            overflow: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {unique.map((z) => {
            const snap = snapshots[z];
            const num = metricNumeric(snap, focusMetric);
            const pending = loadingAcs && !(z in snapshots);
            const secondary = pending
              ? "…"
              : formatMetricDisplay(num, metricMeta?.format ?? "percent");
            return (
              <ListItem
                key={z}
                secondaryAction={
                  <Tooltip title="Remove from list">
                    <span>
                      <IconButton
                        edge="end"
                        aria-label={`Remove ZCTA ${z}`}
                        size="small"
                        disabled={disabled}
                        onClick={() => removeZip(z)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemText primary={z} secondary={secondary} />
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No ZCTAs yet. Use the map or a saved collection to add codes.
        </Typography>
      )}
    </Box>
  );
};

export default ZipTargetingField;
